import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchContent } from "@/lib/api";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import CtaBand from "@/components/sections/CtaBand";
import { UI } from "@/content/site";

const CATEGORY_ORDER = ["divorce", "child-custody", "child-support", "property", "prenup", "adoption", "domestic-violence", "business-law"];

// Module-level constants for CtaBand fallbacks so JSX prop identity is stable across renders.
const BLOG_INDEX_CTA_EN = {
  heading: "Have questions about your case?",
  paragraph: "Call 214-466-6699 for a free bilingual consultation.",
};
const BLOG_INDEX_CTA_ES = {
  heading: "¿Tiene preguntas sobre su caso?",
  paragraph: "Llame al 214-466-6699 para una consulta gratis bilingüe.",
};

export default function BlogIndex() {
  const lang = useLang();
  const path = lang === "es" ? "/es/blog" : "/blog";
  const ui = UI[lang];
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const perPage = 9;

  const { data, isLoading } = useQuery({
    queryKey: ["content", path],
    queryFn: () => fetchContent(path),
    staleTime: 5 * 60_000,
  });

  const posts = useMemo(() => data?.page?.blog_posts || [], [data]);
  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category).filter(Boolean));
    return CATEGORY_ORDER.filter((c) => set.has(c));
  }, [posts]);

  const filtered = category === "all" ? posts : posts.filter((p) => p.category === category);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const shown = filtered.slice((page - 1) * perPage, page * perPage);
  const featured = shown[0];
  const rest = shown.slice(1);

  if (isLoading || !data) return <div className="gw-container gw-section"><p>Loading…</p></div>;

  return (
    <>
      <Seo page={data.page} path={path} lang={lang} pair={data.pair} schema={data.schema} />
      <section className="gw-container gw-section">
        <Breadcrumbs items={data.page.breadcrumbs || []} lang={lang} />
        <h1>{data.page.h1}</h1>
        <p className="gw-muted" style={{fontSize: "1.1rem", maxWidth: 720}}>
          {data.page.sections?.[0]?.paragraphs?.[0]}
        </p>

        <div className="gw-blog-filters">
          <button className={`gw-blog-chip ${category === "all" ? "active" : ""}`} onClick={() => { setCategory("all"); setPage(1); }}>
            {lang === "en" ? "All Topics" : "Todos los Temas"}
          </button>
          {categories.map((c) => {
            const label = posts.find((p) => p.category === c)?.category_label || c;
            return (
              <button key={c} className={`gw-blog-chip ${category === c ? "active" : ""}`} onClick={() => { setCategory(c); setPage(1); }}>
                {label}
              </button>
            );
          })}
        </div>

        {featured && (
          <Link to={featured.url} className="gw-blog-featured gw-card gw-card-link">
            <div className="gw-blog-featured-body">
              <span className="gw-eyebrow">{lang === "en" ? "Featured" : "Destacado"} · {featured.category_label}</span>
              <h2>{featured.slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</h2>
              <p className="gw-muted">
                {new Date(featured.date).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "short", day: "numeric" })}
                {featured.viral && <span className="gw-viral-pill">{lang === "en" ? "Trending" : "En Tendencia"}</span>}
              </p>
            </div>
          </Link>
        )}

        <div className="gw-blog-grid">
          {rest.map((p) => (
            <Link key={p.slug} to={p.url} className="gw-card gw-card-link gw-blog-card">
              <span className="gw-blog-category-chip">{p.category_label}</span>
              <h3>{p.slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</h3>
              <p className="gw-muted" style={{fontSize: "0.9rem"}}>
                {new Date(p.date).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "short", day: "numeric" })}
                {p.viral && <span className="gw-viral-pill">{lang === "en" ? "Trending" : "Tendencia"}</span>}
              </p>
              <span className="gw-blog-read">{ui.ctaLearnMore} →</span>
            </Link>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="gw-pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button key={n} className={`gw-page-btn ${page === n ? "active" : ""}`} onClick={() => setPage(n)}>{n}</button>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <p className="gw-muted" style={{textAlign: "center", marginTop: 40}}>
            {lang === "en" ? "No posts in this category yet." : "Aún no hay publicaciones en esta categoría."}
          </p>
        )}
      </section>

      <CtaBand section={lang === "en" ? BLOG_INDEX_CTA_EN : BLOG_INDEX_CTA_ES} lang={lang} />

      <style>{`
        .gw-blog-filters {
          display: flex; flex-wrap: wrap; gap: var(--gw-space-2);
          margin: var(--gw-space-6) 0 var(--gw-space-8);
        }
        .gw-blog-chip {
          padding: 0.5rem 1rem;
          border: 1.5px solid var(--gw-border-strong);
          background: transparent;
          border-radius: 999px;
          font-weight: 600; font-size: 0.9rem;
          cursor: pointer;
          color: var(--gw-text-soft);
          transition: all 0.15s ease;
        }
        .gw-blog-chip:hover { border-color: var(--gw-primary); color: var(--gw-primary); }
        .gw-blog-chip.active { background: var(--gw-primary); color: #fff; border-color: var(--gw-primary); }
        .gw-blog-featured { padding: var(--gw-space-8); margin-bottom: var(--gw-space-6); border-left: 5px solid var(--gw-accent); }
        .gw-blog-featured h2 { font-size: 1.75rem; margin: var(--gw-space-3) 0; }
        .gw-blog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--gw-space-5); }
        .gw-blog-card { padding: var(--gw-space-5); display: flex; flex-direction: column; }
        .gw-blog-card h3 { font-size: 1.15rem; margin: var(--gw-space-2) 0; flex-grow: 1; }
        .gw-blog-category-chip { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--gw-accent-hover); background: #FFF9E8; padding: 4px 10px; border-radius: 999px; margin-bottom: var(--gw-space-2); }
        .gw-blog-read { color: var(--gw-primary); font-weight: 600; font-size: 0.9rem; margin-top: var(--gw-space-2); }
        .gw-viral-pill { display: inline-block; margin-left: 8px; padding: 3px 10px; background: var(--gw-accent); color: var(--gw-primary-dark); font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border-radius: 999px; }
        .gw-pagination { display: flex; gap: 8px; justify-content: center; margin-top: var(--gw-space-8); }
        .gw-page-btn { padding: 8px 14px; border: 1px solid var(--gw-border-strong); background: #fff; border-radius: var(--gw-radius); cursor: pointer; font-weight: 600; }
        .gw-page-btn.active { background: var(--gw-primary); color: #fff; border-color: var(--gw-primary); }
      `}</style>
    </>
  );
}
